import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AlertComponent } from '@components';
import { Contract } from '@models';
import { Store } from '@ngrx/store';
import { UtilService } from '@services';
import {
  createContract,
  createContractFailed,
  createContractSuccess,
  fetchLatestContract,
  selectContract,
  updateContract,
  updateContractFailed,
  updateContractSuccess,
} from '@state';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    AlertComponent,
  ],
  templateUrl: './contract.page.html',
  styleUrl: './contract.page.scss',
})
export class ContractPage implements OnInit {
  private contract: Contract | null = null;
  public contractForm!: FormGroup;
  public isEditMode = false;
  public selectedFile?: File;
  public uploadPreview?: SafeResourceUrl;
  public loading = false;
  public dragActive = false;
  public error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private sanitizer: DomSanitizer,
    private utils: UtilService
  ) {}

  ngOnInit(): void {
    this.store.select(selectContract).subscribe((contract) => {
      this.contract = contract;
      this.contractForm = this.fb.group({
        version: [this.contract?.version, Validators.required],
        title: [this.contract?.title, Validators.required],
        description: [this.contract?.description, Validators.required],
      });
      if (this.contract?.fileUrl)
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.contract.fileUrl
        );
      this.isEditMode = !!this.contract;
    });

    this.store.dispatch(fetchLatestContract());
  }

   public onFileSelected(event: Event): void {
    try {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.selectedFile = target.files[0];
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(this.selectedFile)
        );
        this.error = null;
      }
    } catch (e) {
      console.error('Error selecting file:', e);
      this.error = 'Failed to select file. Please try again.';
    }
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragActive = true;
  }

  public onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
  }

  public onFileDrop(event: DragEvent) {
    try {
      event.preventDefault();
      this.dragActive = false;
      if (event.dataTransfer && event.dataTransfer.files.length > 0) {
        this.selectedFile = event.dataTransfer.files[0];
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(this.selectedFile)
        );
        this.error = null;
      }
    } catch (e) {
      console.error('Error dropping file:', e);
      this.error = 'Failed to drop file. Please try again.';
    }
  }

  public closePreview(): void {
    this.uploadPreview = undefined;
    this.selectedFile = undefined;
  }

  async onSubmit(): Promise<void> {
    try {
      if (this.contractForm.invalid) return;
      this.loading = true;
      const contractData: any = this.contractForm.value;
      await this.saveContract(contractData);
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    } finally {
      this.loading = false;
    }
  }

  async saveContract(contract: Contract): Promise<void> {
    if (this.isEditMode) await this.updateContract(contract);
    else await this.createContract(contract);
  }

  private async createContract(contract: Contract): Promise<void> {
    await this.utils.actionsToPromise(
      createContract({ contract, file: this.selectedFile }),
      createContractSuccess,
      createContractFailed
    );
  }

  private async updateContract(contract: Contract): Promise<void> {
    await this.utils.actionsToPromise(
      updateContract({
        contract: { ...this.contract, ...contract },
        file: this.selectedFile,
      }),
      updateContractSuccess,
      updateContractFailed
    );
  }
}
